import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import ExoDialog from '../exo/ExoDialog';
import { useState } from 'react';
import { NewEmailIntegration } from '../new/NewEmailIntegration';
import Cookies from 'universal-cookie';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { ProjectAddElementButton } from '../exo/project/ProjectAddElementButton';
import DeleteIcon from '@mui/icons-material/Delete';
import AddModeratorIcon from '@mui/icons-material/AddModerator';
import {
  API_DELETE,
  API_GET,
  API_PATCH,
  DBRequest,
  useConfig,
} from '../../api/api';
import { NewSignatureCertificate } from '../new/NewSignatureCertificate';
import { updateOrCreate } from '../special/updateOrCreate';
import Feedback from '../special/Feedback';
import { set } from 'date-fns';
import DateTimeLabel from '../label/DateTimeLabel';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import GppBadIcon from '@mui/icons-material/GppBad';
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined';

export const ECSettings = ({ onInboxChange, credentials, onDelete }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const cookies = new Cookies();
  const userId = cookies.get('userId');

  const [edit, setEdit] = useState(null);
  const [openNew, setOpenNew] = useState(false);

  // manage certificate dialog states
  const [openNewCertificate, setOpenNewCertificate] = useState(false);
  const [editCertificate, setEditCertificate] = useState(null);

  // to show all the certificates for a specific email credential (it stored the selected email credential data)
  const [showCert, setShowCert] = useState(null);

  // to store the certificate data when fetching for a specific email credential
  const [certificateData, setCertificateData] = useState(null);

  const [isLoading, setIsLoading] = useState(false);

  // feedback
  const [alertState, setAlertState] = useState({
    alertOpen: false,
    alertType: 'info',
    alertText: 'test',
  });
  function handleFeedback(text, type) {
    setAlertState({ alertOpen: true, alertText: text, alertType: type });
    setIsLoading(false);
  }

  function handleDelete(cred) {
    DBRequest({
      config,
      path: `email-credentials/${cred.id}`,
      method: API_DELETE(),
      onResponse: () => {
        onDelete(cred);
      },
      onFeedback: handleFeedback,
    });
  }

  // update local certificate after trust toggle response
  function handleTrustChange(updatedCert) {
    setCertificateData((prev) =>
      prev.map((c) => (c.id === updatedCert.id ? { ...c, ...updatedCert } : c)),
    );
  }

  // to delete the certificate
  function handleCertificateDelete(cert) {
    setIsLoading(true);
    DBRequest({
      config,
      path: `certificates/${cert.id}`,
      method: API_DELETE(),
      onResponse: () => {
        setIsLoading(false);
        setCertificateData((prev) => {
          // Filter out the certificate with the matching id
          return prev.filter((e) => e.id !== cert.id);
        });
      },
      onFeedback: handleFeedback,
    });
  }

  // to fetch the certificates for a specific email credential and show the certificate dialog
  function handleSelect(selectedEmail) {
    DBRequest({
      config,
      path: `certificates?email[eq]=${selectedEmail?.smtpUsername}`,
      method: API_GET,
      onResponse: (res) => {
        setCertificateData(res.data);
        setShowCert(selectedEmail);
      },
    });
  }

  //update state after adding or editing a certificate
  function handleCertificateChange(res) {
    const newCertificateData = updateOrCreate(certificateData, res.data.data);
    setCertificateData(newCertificateData);
  }

  return (
    <Box className="flex flex-col gap-2">
      <Typography variant="h4">{t('E-Mail Accounts')}</Typography>
      {credentials?.map((cred) => (
        <Cred
          key={cred.id}
          data={cred}
          onSelect={setEdit}
          onDelete={handleDelete}
          onShowCertificate={handleSelect} // to show cert dialog
        />
      ))}
      <ProjectAddElementButton
        label={t('Add E-Mail')}
        sizePx="40px"
        onClick={() => setOpenNew(true)}
      />
      <ExoDialog
        open={Boolean(edit) || openNew}
        onClose={() => {
          setOpenNew(false);
          setEdit(null);
        }}
        limitWidth
      >
        <NewEmailIntegration
          onChange={onInboxChange}
          startData={edit}
          userid={userId}
          onDelete={onDelete}
          onClose={() => {
            setOpenNew(false);
            setEdit(null);
          }}
        />
      </ExoDialog>
      {/* Certificate Dialog */}
      <ExoDialog
        open={Boolean(showCert)}
        onClose={() => {
          setShowCert(null);
        }}
        limitWidth
      >
        <Box className="flex flex-col gap-2">
          <Typography variant="h4">{t('Certificates')}</Typography>
          {/* Add Certificate Button */}
          <ProjectAddElementButton
            label={t('Add Certificate')}
            sizePx="40px"
            onClick={() => setOpenNewCertificate(true)}
          />
          {certificateData?.map((cert) => (
            <ShowSignatureCertificate
              key={cert.id}
              data={cert}
              onSelect={setEditCertificate}
              onDelete={handleCertificateDelete}
              onTrustChange={handleTrustChange}
              onFeedback={handleFeedback}
              setOpenNewCertificate={setOpenNewCertificate}
            />
          ))}
        </Box>
        <Feedback setState={setAlertState} state={alertState} />
      </ExoDialog>

      {/* dialog to add or edit the certificate */}
      <ExoDialog
        open={Boolean(editCertificate) || openNewCertificate}
        onClose={() => {
          setEditCertificate(null);
          setOpenNewCertificate(false);
        }}
        limitWidth
      >
        <NewSignatureCertificate
          onChange={handleCertificateChange}
          startData={
            Boolean(editCertificate)
              ? editCertificate
              : { email: showCert?.imapUsername }
          }
          userid={userId}
          onClose={() => {
            setEditCertificate(null);
            setOpenNewCertificate(false);
          }}
        />
      </ExoDialog>
    </Box>
  );
};

const Cred = ({ data, onSelect, onDelete, onShowCertificate }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);

  const { t } = useTranslation();

  const { label, imapUsername, imapStatus, smtpStatus } = data;

  const imapColor = imapStatus === 1 ? colors.success : colors.error;
  const smtpColor = smtpStatus === 1 ? colors.success : colors.error;
  return (
    <Box
      className="flex items-center"
      sx={{
        '& .delete-btn-wrapper': {
          opacity: 0,
          width: 0,
          overflow: 'hidden',
          transition: 'opacity 0.2s, width 0.2s, margin 0.2s',
        },
        '&:hover .delete-btn-wrapper': {
          opacity: 1,
          width: '48px',
          marginLeft: '8px',
        },
      }}
    >
      <Button
        onClick={() => onSelect(data)}
        sx={{
          flex: 1,
          minWidth: 0,
          alignItems: 'start',
          flexDirection: 'column',
          bgcolor: colors.card,
          border: `1px solid transparent`,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
        }}
      >
        <Box className="flex gap-2 items-center">
          <Typography fontWeight={600}>{label}</Typography>
          <Typography fontWeight={400} className="opacity-50" fontSize={12}>
            {`<${imapUsername}>`}
          </Typography>
        </Box>
        <Typography fontWeight={500} fontSize="12px" sx={{ color: imapColor }}>
          {t('incoming server')}
          <span className="ml-2">
            {imapStatus === 1 ? t('Connected') : t('Connection failed')}
          </span>
        </Typography>
        <Typography fontWeight={500} fontSize="12px" sx={{ color: smtpColor }}>
          {t('outgoing server')}
          <span className="ml-2">
            {smtpStatus === 1 ? t('Connected') : t('Connection failed')}
          </span>
        </Typography>
      </Button>
      <Box className="delete-btn-wrapper flex items-center">
        <Tooltip title={t('Manage certificates')}>
          <IconButton
            onClick={() => onShowCertificate(data)}
            sx={{ color: colors.info }}
          >
            <AddModeratorIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Box className="delete-btn-wrapper flex items-center">
        <Tooltip title={t('Delete account')}>
          <IconButton
            onClick={() => onDelete(data)}
            sx={{ color: colors.error }}
          >
            <DeleteIcon />
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

const ShowSignatureCertificate = ({
  onSelect,
  data,
  onDelete,
  onTrustChange,
  onFeedback,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const config = useConfig();
  const [isProcessing, setIsProcessing] = useState(false);

  const { t } = useTranslation();

  const {
    email,
    issuer,
    fingerprintSha256,
    validFrom,
    validTo,
    isValid,
    isTrusted,
    trustLevel,
  } = data;

  // Determine the trust icon based on trust level
  const trustIcon =
    trustLevel === 'untrusted' ? (
      <GppBadIcon sx={{ fontSize: '16px' }} />
    ) : trustLevel === 'manually_trusted' ? (
      <VerifiedUserOutlinedIcon sx={{ fontSize: '16px' }} />
    ) : (
      <VerifiedUserIcon sx={{ fontSize: '16px' }} />
    );

  // Determine the trust color based on trust level
  const trustColor = trustLevel === 'untrusted' ? colors.error : colors.success;

  // Only allow toggling trust for manually_trusted and untrusted certificates
  const isToggleable =
    trustLevel === 'manually_trusted' || trustLevel === 'untrusted';

  // Handle trust toggle click
  function handleTrustClick(e) {
    e.preventDefault();
    e.stopPropagation();
    const nextTrustLevel =
      trustLevel === 'manually_trusted' ? 'untrusted' : 'manually_trusted';
    DBRequest({
      config,
      path: `certificates/${data.id}`,
      method: API_PATCH({ trustLevel: nextTrustLevel }),
      onResponse: (res) => {
        const updated = res?.data?.data ?? {
          ...data,
          trustLevel: nextTrustLevel,
        };
        onTrustChange(updated);
      },
      onLoading: setIsProcessing,
      onFeedback,
    });
  }

  return (
    <Box
      className="flex items-center"
      sx={{
        '& .delete-btn-wrapper': {
          opacity: 0,
          width: 0,
          overflow: 'hidden',
          transition: 'opacity 0.2s, width 0.2s, margin 0.2s',
        },
        '&:hover .delete-btn-wrapper': {
          opacity: 1,
          width: '48px',
          marginLeft: '8px',
        },
      }}
    >
      <Button
        onClick={() => onSelect(data)} // enable when edit functionality is needed
        sx={{
          flex: 1,
          minWidth: 0,
          alignItems: 'start',
          flexDirection: 'column',
          bgcolor: colors.card,
          border: `1px solid transparent`,
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
          },
        }}
      >
        <Box className="flex gap-2 items-center">
          <Typography
            fontWeight={600}
            sx={{ color: isValid ? colors.success : colors.error }}
          >
            {email}
          </Typography>
          {isToggleable ? (
            <Tooltip
              title={
                trustLevel === 'manually_trusted'
                  ? t('Mark as untrusted')
                  : t('Mark as trusted')
              }
            >
              <IconButton
                size="small"
                sx={{ color: trustColor }}
                onClick={handleTrustClick}
              >
                {isProcessing ? (
                  <CircularProgress size="14px" color="inherit" />
                ) : (
                  trustIcon
                )}
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title={t('System trusted')}>
              <Box
                sx={{
                  color: trustColor,
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                {trustIcon}
              </Box>
            </Tooltip>
          )}
        </Box>

        {/* Display issuer email */}
        <Typography fontWeight={500} fontSize="12px">
          {t('Issuer')}
          <span className="ml-2">{issuer}</span>
        </Typography>

        {/* Display fingerprint */}
        <Typography fontWeight={500} fontSize="12px">
          {t('Fingerprint')}
          <span className="ml-2">{fingerprintSha256}</span>
        </Typography>

        {/* Display Valid From and Valid To */}
        <Typography fontWeight={500} fontSize="12px">
          {t('Valid from')}
          <span className="ml-2">
            <DateTimeLabel dateTime={validFrom} />
          </span>
        </Typography>
        <Typography fontWeight={500} fontSize="12px">
          {t('Valid until')}
          <span className="ml-2">
            <DateTimeLabel dateTime={validTo} />
          </span>
        </Typography>
      </Button>

      {/* Delete button */}
      <Box className="delete-btn-wrapper flex items-center">
        <IconButton onClick={() => onDelete(data)} sx={{ color: colors.error }}>
          <DeleteIcon />
        </IconButton>
      </Box>
    </Box>
  );
};
